import React, { useState } from 'react';
import {
  Star,
  ChevronDown,
  ChevronRight,
  Edit3,
  Save,
  X
} from 'lucide-react';

import {
  FieldConfig,
  SectionConfig,
  TabConfig
} from '../../types/uiConfig';
import {
  mockFieldConfigs,
  mockSectionConfigs,
  mockTabConfigs
} from '../../data/mockData';

interface DynamicClientDetailProps {
  clientData: any;
  userRole: string;
  onBack?: () => void;
}

const DynamicClientDetail: React.FC<DynamicClientDetailProps> = ({ clientData, userRole, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(clientData || {});

  // Load configurations
  const fields = mockFieldConfigs;
  const sections = mockSectionConfigs;
  const tabs = mockTabConfigs;

  // Mock client data if not provided
  const defaultClientData = {
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@email.com',
    phone: '(555) 123-4567',
    status: 'active',
    creditScore: 647,
    enrollmentDate: '2025-03-15',
    services: ['credit_repair', 'credit_monitoring']
  };

  const currentData = formData || defaultClientData;

  const toggleSection = (sectionId: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionId)) {
      newCollapsed.delete(sectionId);
    } else {
      newCollapsed.add(sectionId);
    }
    setCollapsedSections(newCollapsed);
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev: Record<string, any>) => ({ ...prev, [fieldName]: value }));
  };

  const renderField = (field: FieldConfig) => {
    if (!field.isVisible) return null;

    const value = currentData[field.name] || field.defaultValue || '';
    const isReadOnly = field.isReadOnly || (!editMode && field.type !== 'formula');

    const commonProps = {
      id: field.name,
      value: value,
      onChange: (e: any) => handleFieldChange(field.name, e.target.value),
      disabled: isReadOnly,
      required: field.isRequired,
      placeholder: field.placeholder,
      className: `w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isReadOnly ? 'bg-gray-50 dark:bg-gray-700' : ''
      }`
    };

    const renderInput = () => {
      switch (field.type) {
        case 'text':
        case 'email':
        case 'url':
          return <input type={field.type} {...commonProps} />;

        case 'phone':
          return <input type="tel" {...commonProps} />;

        case 'number':
          return <input type="number" {...commonProps} min={field.validation?.min} max={field.validation?.max} />;

        case 'currency':
          return (
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
              <input type="number" {...commonProps} className={`${commonProps.className} pl-8`} step="0.01" />
            </div>
          );

        case 'date':
          return <input type="date" {...commonProps} />;

        case 'datetime':
          return <input type="datetime-local" {...commonProps} />;

        case 'textarea':
          return <textarea rows={3} {...commonProps} />;

        case 'select':
          return (
            <select {...commonProps}>
              <option value="">Select an option...</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option.value} disabled={!option.isActive}>
                  {option.label}
                </option>
              ))}
            </select>
          );

        case 'multiselect':
          return (
            <div className="space-y-2">
              {field.options?.map((option, index) => (
                <label key={index} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={Array.isArray(value) ? value.includes(option.value) : false}
                    onChange={(e) => {
                      const currentValues = Array.isArray(value) ? value : [];
                      const newValues = e.target.checked
                        ? [...currentValues, option.value]
                        : currentValues.filter(v => v !== option.value);
                      handleFieldChange(field.name, newValues);
                    }}
                    disabled={isReadOnly}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-900 dark:text-gray-100">{option.label}</span>
                </label>
              ))}
            </div>
          );

        case 'checkbox':
          return (
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={value === true || value === 'true'}
                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                disabled={isReadOnly}
                className="mr-2"
              />
              <span className="text-gray-900 dark:text-gray-100">{field.label}</span>
            </label>
          );

        case 'radio':
          return (
            <div className="space-y-2">
              {field.options?.map((option, index) => (
                <label key={index} className="flex items-center">
                  <input
                    type="radio"
                    name={field.name}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    disabled={isReadOnly}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-900 dark:text-gray-100">{option.label}</span>
                </label>
              ))}
            </div>
          );

        case 'formula':
          return (
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">
              {value || '(Calculated value)'}
            </div>
          );

        default:
          return <input type="text" {...commonProps} />;
      }
    };

    return (
      <div className={`col-span-${field.columnWidth}`} key={field.id}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {field.label}
          {field.isRequired && <span className="text-red-500 ml-1">*</span>}
          {field.isCustom && (
            <span className="ml-1 px-1 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">
              Custom
            </span>
          )}
        </label>
        {renderInput()}
        {field.helpText && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{field.helpText}</p>
        )}
      </div>
    );
  };

  const renderSection = (section: SectionConfig) => {
    if (!section.isVisible) return null;

    const sectionFields = fields
      .filter(field => field.section === section.id && field.isVisible)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    if (sectionFields.length === 0) return null;

    const isCollapsed = collapsedSections.has(section.id);

    return (
      <div key={section.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <div
          className={`px-6 py-4 border-b border-gray-200 dark:border-gray-600 cursor-pointer ${
            section.isCollapsible ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : ''
          }`}
          onClick={() => section.isCollapsible && toggleSection(section.id)}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{section.label}</h3>
              {section.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400">{section.description}</p>
              )}
            </div>
            {section.isCollapsible && (
              <div className="text-gray-400 dark:text-gray-500">
                {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>
            )}
            {section.isCustom && (
              <span className="px-2 py-1 bg-purple-100 text-purple-600 text-xs rounded">
                Custom Section
              </span>
            )}
          </div>
        </div>

        {!isCollapsed && (
          <div className="p-6">
            <div className={`grid grid-cols-12 gap-4`}>
              {sectionFields.map(field => renderField(field))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = (tab: TabConfig) => {
    const tabSections = sections
      .filter(section => tab.sections.includes(section.id))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    return (
      <div className="space-y-6">
        {tabSections.map(section => renderSection(section))}
      </div>
    );
  };

  const visibleTabs = tabs
    .filter(tab => tab.isVisible)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                >
                  ← Back
                </button>
              )}
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                  {currentData.firstName?.[0]}{currentData.lastName?.[0]}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {currentData.firstName} {currentData.lastName}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">{currentData.email}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {editMode ? (
                <>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      // Here you would typically save the data
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setFormData(clientData || defaultClientData);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Demo Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-8">
          <div className="flex items-start">
            <Star className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">Dynamic UI Configuration Demo</h3>
              <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                This page is rendered dynamically based on field and section configurations.
                Custom fields, sections, and tabs are displayed automatically.
                Notice the "Custom" badges on fields and sections added through the UI Configuration Manager.
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-600">
            <nav className="flex space-x-8 px-6">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.name)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.name
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <span>{tab.label}</span>
                  {tab.isCustom && (
                    <span className="px-1 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">
                      Custom
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {visibleTabs
              .filter(tab => tab.name === activeTab)
              .map(tab => (
                <div key={tab.id}>
                  {renderTabContent(tab)}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicClientDetail;